import { useAuthStore } from '@/stores/auth';
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type RawAxiosRequestHeaders,
  type RawAxiosRequestConfig
} from 'axios';
import ls from 'localstorage-ttl';

interface ApiHeaders extends RawAxiosRequestHeaders {
  Accept: string;
  Authorization?: string;
}

export interface ApiOptions extends RawAxiosRequestConfig {
  headers?: ApiHeaders;
  /** true のとき 5xx の汎用トーストを出さない（意図的な up_err 更新など） */
  skipErrorToast?: boolean;
}

export type ApiResponse = AxiosResponse;

type ToastInstance = { error: (msg: string, opts?: { timeout?: number }) => void };
let toastInstance: ToastInstance | null = null;

export function setApiToast(toast: ToastInstance) {
  toastInstance = toast;
}

const parseRequestData = (data: unknown): Record<string, unknown> | null => {
  if (!data) return null;
  if (typeof data === 'object' && !(data instanceof FormData) && !ArrayBuffer.isView(data)) {
    return data as Record<string, unknown>;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
};

/** アップロード失敗ステータス更新など、5xx でも汎用トーストを出さないケース */
const shouldSkipServerErrorToast = (error: AxiosError) => {
  const config = error.config as ApiOptions | undefined;
  if (config?.skipErrorToast) return true;

  const requestBody = parseRequestData(config?.data);
  if (requestBody?.status === 'up_err') return true;

  const responseData = error.response?.data;
  if (responseData && typeof responseData === 'object') {
    const body = responseData as Record<string, unknown>;
    if (body.status === 'up_err') return true;
    const nested = body.data;
    if (nested && typeof nested === 'object' && (nested as { status?: string }).status === 'up_err') {
      return true;
    }
  }
  return false;
};

const headers: ApiHeaders = {
  Accept: 'application/json'
};
const API: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  headers: headers,
  timeout: import.meta.env.TIME_OUT_MM_SECONDS ?? 120000
});

API.interceptors.request.use(
  (config) => {
    const loginToken: string | null = ls.get('tc_access_token');
    if (loginToken) {
      config.headers.Authorization = `Token ${loginToken}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);
API.interceptors.response.use(
  (response: ApiResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const { config, response, message } = error;
    const method = config?.method?.toUpperCase();
    const url = config?.url;
    const status = response?.status;
    const statusText = response?.statusText;
    const authStore = useAuthStore();
    // レスポンスボディは内容によってログに出力するか判断 (機密情報注意)
    // const responseData = response?.data;

    console.error('🚨 API Response Error:');
    console.error(`  [${method}] ${url}`);
    console.error(`  Status: ${status} ${statusText || ''}`);
    if (message) {
      console.error(`  Message: ${message}`);
    }
    if (error.response?.status === 401) {
      authStore.forceLogoutAndRedirect(
        'セッションがタイムアウトしました。再度ログインしてください。'
      );
    }
    if (
      error.response?.status &&
      error.response.status / 100 === 5 &&
      !shouldSkipServerErrorToast(error)
    ) {
      toastInstance?.error(
        'サーバーエラーが発生しました。サポートへお問い合わせください。',
        { timeout: 3000 }
      );
    }
    return Promise.reject(error);
  }
);
export default API;
